import { createSelector } from 'reselect';
import { RootState } from '../../../../types';
import { EntityTypes } from '../../../../constants/entities';
import { IActionDAO } from 'types/domain/action';
import { IEntityReducer } from '../../../reducers/entities';
import { Handles } from 'constants/handles';
import { parseActionDAO } from './action';
import { IFeed, selectFeed } from '../feed';
import { getEntities } from 'core/selectors/utils/getEntities';
import { getFirstAction } from './actionFirst';
import { selectActionHistory } from './actionHistory';
import { IModuleAssign, IModuleOut, ModuleType } from 'types/domain/module';
import { IRegisterReducer } from 'core/slices/register';
import { IStateReducer } from 'core/reducers/state';

export interface IActionFeed {
  feed: IActionFeedItem[];
  index: number;
  next: boolean;
}

export interface IActionFeedItem {
  actionId?: number;
  sessionAttemptId?: number;
  courseProgressId?: number;
  actionRegisterId: string;
  verificationRequestId?: number;
  courseNodeId?: number;
  moduleId?: number;
  visible?: boolean;
}

const state = (state: RootState) => state.state;
const entity = (state: RootState) => state.entity.present;
const feed = selectFeed;
const actionHistory = selectActionHistory;
const register = (state: RootState) => state.register;
const uploads = (state: RootState) => state.core.upload.uploads;

export const selectActionFeed = createSelector(
  [state, entity, feed, actionHistory, uploads, register],
  parseActionFeed,
);

export function parseActionFeed(
  state: IStateReducer,
  entity: IEntityReducer,
  feed: IFeed,
  history: IActionFeedItem[],
  uploads,
  register: IRegisterReducer,
): IActionFeed {
  const future = [];

  if (state[EntityTypes.ACTION])
    addActionToFuture(
      state[EntityTypes.SESSION_ATTEMPT],
      entity,
      parseActionDAO(entity, state[EntityTypes.ACTION], uploads),
      future,
      history,
      true,
    );

  feed.feed
    .filter(item => (item.sessionAttemptId ? !register.attempts[item.sessionAttemptId] : true))
    .filter(item => (item.courseNodeId ? !register.dialogues[item.courseNodeId] : true))
    .forEach(next => {
      if (next.moduleId) nextModule(next.sessionAttemptId, entity, next.moduleId, future, history);
      if (next.verificationRequestId) nextVerificationRequest(entity, next.verificationRequestId, future);
      if (next.courseNodeId) nextDialogue(entity, next.courseNodeId, future);
    });

  return {
    feed: [...history, ...future],
    index: register.index,
    next: register.isNext,
  } as IActionFeed;
}

function processModOut(entity, modOut: IModuleOut) {
  if (!modOut) return null;

  return parseActionDAO(entity, modOut.targetId);
}

function addActionToFuture(
  sessionAttemptId: number,
  entity: IEntityReducer,
  action: IActionDAO,
  future: IActionFeedItem[],
  history: IActionFeedItem[],
  first: boolean,
) {
  if (!action) return;


  if (!first && action.id !== history[history.length - 1]?.actionId) {
    future.push({
      actionId: action.id,
      moduleId: action.moduleId,
      visible: !action.sceneId,
      sessionAttemptId: sessionAttemptId,
      actionRegisterId: null,
    });
  }

  const next = processModOut(entity, action.outs[Handles.DEFAULT]);
  if (next && next.status > -1) addActionToFuture(sessionAttemptId, entity, next, future, history, false);
  else if (action.childId && action.childId !== action.id && !future.some(f => f.actionId === action.childId)) {
    const child = parseActionDAO(entity, action.childId);
    if (child && child.status > -1) return addActionToFuture(sessionAttemptId, entity, child, future, history, false);
  }
}

function nextModule(sessionAttemptId, entity, moduleId, future, history) {
  const module = entity[EntityTypes.MODULE][moduleId];

  if (!module) return null;

  if (![ModuleType.STANDARD].includes(module.type)) {
    future.push({
      url: module.url,
      moduleId: module.id,
      sessionAttemptId: module.sessionAttemptId || sessionAttemptId,
    });
    return;
  }


  if (module.actionId)
    return addActionToFuture(sessionAttemptId, entity, parseActionDAO(entity, module.actionId), future, history, false);

  const actions: IActionDAO[] = getEntities(entity, EntityTypes.ACTION, 'moduleId', moduleId).map(a =>
    parseActionDAO(entity, a.id),
  );

  if (actions) {
    console.log(actions);
    const firstAction = getFirstAction(actions);
    console.log(firstAction);
    if (firstAction)
      addActionToFuture(
        module.sessionAttemptId || sessionAttemptId,
        entity,
        parseActionDAO(entity, firstAction.id),
        future,
        history,
        false,
      );
  }


}

function nextVerificationRequest(entity, verificationRequestId, future) {
  // const verificationRequest: IVerificationRequest = parseVerificationRequestDAO(entity, verificationRequestId);
  future.push({ verificationRequestId });
}

function nextDialogue(entity, courseNodeId, future) {
  // const courseNode: ICourseNodeDAO = parseCourseNodeDAO(entity, courseNodeId);
  future.push({ courseNodeId });
}
